import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchProjectById,
  updateProject,
  addProjectMember,
  removeProjectMember
} from '../../store/slices/projectSlice';
import { fetchTasks, deleteTask } from '../../store/slices/taskSlice';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import StatusBadge from '../../components/StatusBadge';
import CommentSection from '../../components/CommentSection';
import TaskModal from './TaskModal';
import TaskDetailModal from './TaskDetailModal';
import { PROJECT_STATUS, COMMENT_ENTITY_TYPES } from '../../utils/constants';
import { formatDateTime } from '../../utils/formatDate';
import api from '../../services/api';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { currentProject, loading } = useSelector((state) => state.projects);
  const { tasks, loading: tasksLoading } = useSelector((state) => state.tasks);

  const [editing, setEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({});
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedNewMember, setSelectedNewMember] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [taskStatusFilter, setTaskStatusFilter] = useState('');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (id) {
      dispatch(fetchProjectById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id) {
      dispatch(fetchTasks({ project: id, status: taskStatusFilter, limit: 50 }));
    }
  }, [dispatch, id, taskStatusFilter]);

  useEffect(() => {
    if (currentProject) {
      setFormData({
        name: currentProject.name,
        description: currentProject.description || '',
        status: currentProject.status
      });
    }
  }, [currentProject]);

  useEffect(() => {
    const fetchAllEmployees = async () => {
      try {
        const response = await api.get('/employees', { params: { limit: 1000 } });
        const employees = response.data.data?.employees || [];
        setAllEmployees(Array.isArray(employees) ? employees : []);
      } catch (err) {
        console.error('Failed to load employees:', err);
      }
    };
    fetchAllEmployees();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      await dispatch(updateProject({ id, data: formData })).unwrap();
      setEditing(false);
    } catch (error) {
      alert('Failed to update project: ' + error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedNewMember) return;
    setAddingMember(true);
    try {
      await dispatch(addProjectMember({ id, employeeId: selectedNewMember })).unwrap();
      setSelectedNewMember('');
    } catch (error) {
      alert('Failed to add member: ' + error);
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (employeeId) => {
    if (!window.confirm('Remove this member from the project?')) return;
    try {
      await dispatch(removeProjectMember({ id, employeeId })).unwrap();
    } catch (error) {
      alert('Failed to remove member: ' + error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await dispatch(deleteTask(taskId)).unwrap();
    } catch (error) {
      alert('Failed to delete task: ' + error);
    }
  };

  const memberIds = currentProject?.members?.map((m) => m._id) || [];
  const availableEmployees = allEmployees.filter((emp) => !memberIds.includes(emp._id));

  if (loading || !currentProject) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid">
        <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/projects')}>
          <i className="ti ti-arrow-left me-1" />
          Back to Projects
        </button>

        {/* Project Info Card */}
        <div className="card mb-3">
          <div className="card-body">
            {editing ? (
              <form onSubmit={handleUpdate}>
                <h2 className="fs-5 mb-3">Edit Project</h2>

                <div className="mb-3">
                  <label className="form-label">Project Name *</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    className="form-control"
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Status *</label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    {Object.values(PROJECT_STATUS).map((status) => (
                      <option key={status} value={status}>
                        {status.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="d-flex gap-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setEditing(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={updating}>
                    {updating ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                  <div>
                    <h2 className="fs-4 mb-2">{currentProject.name}</h2>
                    <StatusBadge status={currentProject.status} />
                  </div>
                  {isAdmin && (
                    <button className="btn btn-outline-primary" onClick={() => setEditing(true)}>
                      Edit
                    </button>
                  )}
                </div>

                {currentProject.description && (
                  <p className="text-muted mt-3 mb-0">{currentProject.description}</p>
                )}

                <div className="row g-3 mt-3">
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">CREATED BY</label>
                    <p className="mb-0">{currentProject.created_by?.name || 'N/A'}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">CREATED</label>
                    <p className="mb-0">{formatDateTime(currentProject.created_at)}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">TOTAL TASKS</label>
                    <p className="mb-0">{currentProject.task_stats?.total || 0}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">COMPLETED TASKS</label>
                    <p className="mb-0">{currentProject.task_stats?.by_status?.completed || 0}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Members Card */}
        <div className="card mb-3">
          <div className="card-header bg-white">
            <h3 className="fs-6 mb-0">Members</h3>
          </div>
          <div className="card-body">
            <div className="d-flex gap-2 flex-wrap mb-3">
              {(currentProject.members || []).length === 0 ? (
                <span className="text-muted small">No members added yet</span>
              ) : (
                currentProject.members.map((member) => (
                  <span key={member._id} className="badge bg-primary d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                    {member.name}
                    {isAdmin && (
                      <button
                        type="button"
                        className="btn-close btn-close-white"
                        style={{ fontSize: '9px' }}
                        aria-label="Remove"
                        onClick={() => handleRemoveMember(member._id)}
                      />
                    )}
                  </span>
                ))
              )}
            </div>

            {isAdmin && (
              <div className="d-flex gap-2">
                <select
                  className="form-select"
                  style={{ maxWidth: '320px' }}
                  value={selectedNewMember}
                  onChange={(e) => setSelectedNewMember(e.target.value)}
                >
                  <option value="">Select an employee to add...</option>
                  {availableEmployees.map((emp) => (
                    <option key={emp._id} value={emp._id}>
                      {emp.name} ({emp.employee_code})
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-outline-primary"
                  onClick={handleAddMember}
                  disabled={!selectedNewMember || addingMember}
                >
                  {addingMember ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tasks Card */}
        <div className="card table-responsive mb-3">
          <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h3 className="fs-6 mb-0">Tasks</h3>
            <div className="d-flex gap-2 align-items-center">
              <select
                className="form-select form-select-sm"
                value={taskStatusFilter}
                onChange={(e) => setTaskStatusFilter(e.target.value)}
                style={{ width: '160px' }}
              >
                <option value="">All Statuses</option>
                <option value="todo">Todo</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="blocked">Blocked</option>
              </select>
              <button className="btn btn-sm btn-primary" onClick={() => setIsTaskModalOpen(true)}>
                <i className="ti ti-plus me-1" />
                Add Task
              </button>
            </div>
          </div>

          {tasksLoading ? (
            <Loading />
          ) : (
            <table className="table mb-0 text-nowrap table-hover">
              <thead className="table-light border-light">
                <tr>
                  <th>Title</th>
                  <th>Assigned To</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Due Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted">
                      No tasks yet
                    </td>
                  </tr>
                ) : (
                  tasks.map((task) => (
                    <tr key={task._id} className="align-middle">
                      <td className="fw-medium">{task.title}</td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          {(task.assigned_to || []).length === 0 ? (
                            <span className="text-muted small">Unassigned</span>
                          ) : (
                            task.assigned_to.map((emp) => (
                              <span key={emp._id} className="badge bg-primary" style={{ fontSize: '11px' }}>
                                {emp.name}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td><StatusBadge status={task.priority} /></td>
                      <td><StatusBadge status={task.status} /></td>
                      <td>{task.due_date ? new Date(task.due_date).toLocaleDateString() : 'N/A'}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setSelectedTaskId(task._id)}
                          >
                            View
                          </button>
                          {(isAdmin || task.created_by?._id === user?._id) && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDeleteTask(task._id)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        <CommentSection
          entityType={COMMENT_ENTITY_TYPES.PROJECT}
          entityId={id}
        />
      </div>

      {isTaskModalOpen && (
        <TaskModal
          projectId={id}
          projectMembers={currentProject.members || []}
          onClose={() => setIsTaskModalOpen(false)}
          onSuccess={() => {
            setIsTaskModalOpen(false);
            dispatch(fetchTasks({ project: id, status: taskStatusFilter, limit: 50 }));
            dispatch(fetchProjectById(id));
          }}
        />
      )}

      {selectedTaskId && (
        <TaskDetailModal
          taskId={selectedTaskId}
          projectMembers={currentProject.members || []}
          onClose={() => setSelectedTaskId(null)}
          onSuccess={() => {
            dispatch(fetchTasks({ project: id, status: taskStatusFilter, limit: 50 }));
            dispatch(fetchProjectById(id));
          }}
        />
      )}
    </Layout>
  );
};

export default ProjectDetail;
